import { useCallback } from 'react';
import { PaymentMethodEnum, ProviderEnum } from '@/types/common';
import { getProviderFromPaymentMethod } from '@/utils/paymentParams';
import {
  isStripeSuccessResponse,
  isStripeFailedResponse,
  isStripeRedirectResponse,
  isNuveiSuccessResponse,
  isBrainTreeSuccessResponse,
  isAdyenFinalizePaymentResponse,
  isAdyenRedirectResponse,
  isPaypalSuccessResponse,
  isNuveiFailedResponse,
  isNuveiRedirectResponse,
  isAdyenFailedResponse,
  isAdyenSuccessResponse,
  isPaypalFailedResponse,
  isBrainTreeFailedResponse,
  isBrainTreeRedirectResponse,
} from '@/types/guards';
import type { PaymentKeys, PaymentMethod } from '@/types/checkout';
import { AppchargeErrorType, type BasePaymentResponse } from '@/types/payment';
import type { RedirectPaymentContext, ThreeDSSecurityState } from '@/types/context';
import { getRedirectPaymentData } from '@/utils/redirectPayment';
import { getCurrentDomain, LocationRoutes } from '@/utils/location';
import { isMobileDevice } from '@/utils/device';
import { adyenService } from '@/services/adyenService';
import { adyenExpressService } from '@/services/adyenExpressService';
import { adyenApplePayService } from '@/services/adyenApplePayService';
import { logInfo } from '@/utils/logger';
import { InfoLogs } from '@/constants/logs';

interface UsePaymentResultsParams {
  paymentMethods: PaymentMethod[];
  onSuccess: (result: BasePaymentResponse, provider: ProviderEnum) => void;
  onError: (appChargeErrorType?: AppchargeErrorType, provider?: ProviderEnum) => void;
  onRedirect: (redirectData: RedirectPaymentContext) => void;
  on3DSRequired: (state: ThreeDSSecurityState) => void;
  actions: {
    stopPaymentProcess: () => void;
  };
}

interface UsePaymentResultsReturn {
  handlePaymentResults: (
    results: unknown,
    selectedPaymentMethod: PaymentMethodEnum,
    paymentKeys: PaymentKeys
  ) => Promise<unknown>;
}

export const usePaymentResults = ({
  paymentMethods,
  onSuccess,
  onError,
  onRedirect,
  on3DSRequired,
  actions,
}: UsePaymentResultsParams): UsePaymentResultsReturn => {
  const handleStripeResponse = useCallback(
    async (
      results: unknown,
      provider: ProviderEnum,
      selectedPaymentMethod: PaymentMethodEnum,
      stripePaymentKey: string
    ) => {
      if (isStripeSuccessResponse(results, provider)) {
        onSuccess(results as BasePaymentResponse, provider);
        return;
      }

      if (isStripeFailedResponse(results, provider)) {
        actions.stopPaymentProcess();
        onError(results?.appChargeErrorType, provider);
        return;
      }

      if (isStripeRedirectResponse(results, provider)) {
        const redirectData = await getRedirectPaymentData(results, ProviderEnum.STRIPE);
        let redirectUrl = redirectData.redirectUrl;
        if (selectedPaymentMethod === PaymentMethodEnum.CASH_APP && isMobileDevice()) {
          redirectUrl = `${getCurrentDomain()}/${LocationRoutes.PAYMENT_HANDLER}/${redirectData.clientSecret}/${stripePaymentKey}`;
        }

        // Wallet 3DS in Stripe (GPay / ApplePay) returns a PI with
        // `next_action.type === 'use_stripe_sdk'` and no `redirect_to_url`.
        // Without this gate they fell through to `<RedirectPayment/>`,
        // which has nothing to redirect to → spinner forever.
        // Route them through the same `<StripeCard3DSForm/>` cards already use
        // (`stripe.handleNextAction(clientSecret)` handles both popup and
        // inline modal variants transparently).
        const requiresStripeNextAction =
          selectedPaymentMethod === PaymentMethodEnum.CREDIT_CARD ||
          selectedPaymentMethod === PaymentMethodEnum.GOOGLE_PAY ||
          selectedPaymentMethod === PaymentMethodEnum.APPLE_PAY;

        if (requiresStripeNextAction) {
          on3DSRequired({
            show3DSForm: true,
            provider: ProviderEnum.STRIPE,
            params: results,
            // Carry the *actual* method (incl. express GPay/ApplePay) so
            // <StripeCard3DSForm/> can pick the correct Stripe instance
            // for multi-MID publishers. Context's `paymentMethodSelected`
            // is unreliable here (stays on default for express flows).
            paymentMethod: selectedPaymentMethod,
            handlePaymentSuccess: () => onSuccess(results as BasePaymentResponse, provider),
            handlePaymentError: () => {
              actions.stopPaymentProcess();
              onError(results?.appChargeErrorType, provider);
            },
          });
          return;
        }
        onRedirect({
          ...redirectData,
          redirectUrl,
          showRedirect: true,
          paymentMethod: selectedPaymentMethod,
          orderId: results.orderId,
          handlePaymentSuccess: () => onSuccess(results as BasePaymentResponse, provider),
        });
      }
    },
    [onSuccess, onError, onRedirect, on3DSRequired, actions]
  );

  const handleNuveiResponse = useCallback(
    async (results: unknown, provider: ProviderEnum) => {
      if (isNuveiSuccessResponse(results, provider)) {
        onSuccess(results as BasePaymentResponse, provider);
      }
      if (isNuveiFailedResponse(results, provider)) {
        actions.stopPaymentProcess();
        onError(results?.appChargeErrorType, provider);
      }
      if (isNuveiRedirectResponse(results, provider)) {
        const redirectData = await getRedirectPaymentData(results, ProviderEnum.NUVEI);
        onRedirect({
          ...redirectData,
          clientSecret: results.sessionToken,
          provider: ProviderEnum.NUVEI,
          handlePaymentSuccess: () => onSuccess(results as BasePaymentResponse, provider),
        });
      }
    },
    [onSuccess, onError, onRedirect, actions]
  );

  const handleBraintreeResponse = useCallback(
    async (results: unknown, provider: ProviderEnum) => {
      if (isBrainTreeSuccessResponse(results, provider)) {
        onSuccess(results as BasePaymentResponse, provider);
      }
      if (isBrainTreeFailedResponse(results, provider)) {
        actions.stopPaymentProcess();
        onError(results?.appChargeErrorType, provider);
      }
      if (isBrainTreeRedirectResponse(results, provider)) {
        const redirectData = await getRedirectPaymentData(results, ProviderEnum.BRAINTREE);
        onRedirect({
          ...redirectData,
          provider: ProviderEnum.BRAINTREE,
          handlePaymentSuccess: () => onSuccess(results as BasePaymentResponse, provider),
        });
      }
    },
    [onSuccess, onError, onRedirect, actions]
  );

  const handleAdyenResponse = useCallback(
    async (results: unknown, provider: ProviderEnum, selectedPaymentMethod: PaymentMethodEnum) => {
      if (isAdyenFinalizePaymentResponse(results)) {
        if (isAdyenRedirectResponse(results)) {
          // Wallets in Adyen (GPay / ApplePay) need `createFromAction` to
          // mount the 3DS2 component — same path cards already use. Without
          // this gate they fell through to `<RedirectPayment/>` and stuck.
          const requiresAdyenAction =
            selectedPaymentMethod === PaymentMethodEnum.CREDIT_CARD ||
            selectedPaymentMethod === PaymentMethodEnum.GOOGLE_PAY ||
            selectedPaymentMethod === PaymentMethodEnum.APPLE_PAY;

          if (requiresAdyenAction) {
            // Express wallets (GPay/ApplePay) keep their native sheet open
            // until the original `onSubmit` SubmitActions resolves. Adyen's
            // native 3DS2 chain renders a challenge modal on our page, but
            // the wallet sheet sits on top and blocks interaction, so the
            // user can never complete the challenge and the sheet times
            // out with REQUEST_TIMEOUT.
            //
            // Per Adyen guidance (Adyen/adyen-web#3315), since we render 3DS
            // in our own container via `createFromAction`, resolve the
            // wallet's SubmitActions with an empty object. This closes the
            // sheet AND prevents the SDK from auto-mounting its own 3DS2
            // component (which would race with ours). The real payment
            // result flows through `/status` after the challenge completes.
            if (selectedPaymentMethod === PaymentMethodEnum.GOOGLE_PAY) {
              adyenExpressService.dismissGooglePaySheet();
            } else if (selectedPaymentMethod === PaymentMethodEnum.APPLE_PAY) {
              adyenApplePayService.dismissApplePaySheet();
            }

            on3DSRequired({
              show3DSForm: true,
              provider: ProviderEnum.ADYEN,
              params: results,
              paymentMethod: selectedPaymentMethod,
              handlePaymentSuccess: () => {
                onSuccess(results as BasePaymentResponse, provider);
              },
              handlePaymentError: () => {
                actions.stopPaymentProcess();
                onError(results?.appChargeErrorType, provider);
              },
            });
          } else {
            const redirectData = await getRedirectPaymentData(results, ProviderEnum.ADYEN);
            onRedirect({
              ...redirectData,
              provider: ProviderEnum.ADYEN,
              handlePaymentSuccess: () => onSuccess(results as BasePaymentResponse, provider),
            });
          }
        }
        if (isAdyenFailedResponse(results)) {
          actions.stopPaymentProcess();
          if (selectedPaymentMethod === PaymentMethodEnum.GOOGLE_PAY) {
            adyenExpressService.completeGooglePayPayment(false);
          } else if (selectedPaymentMethod === PaymentMethodEnum.APPLE_PAY) {
            adyenApplePayService.completeApplePayPayment(false);
          } else {
            adyenService.runPaymentFailed();
          }
          onError(results?.appChargeErrorType, provider);
        }
        if (isAdyenSuccessResponse(results)) {
          if (selectedPaymentMethod === PaymentMethodEnum.GOOGLE_PAY) {
            adyenExpressService.completeGooglePayPayment(true, results.adyen.resultCode);
          } else if (selectedPaymentMethod === PaymentMethodEnum.APPLE_PAY) {
            adyenApplePayService.completeApplePayPayment(true, results.adyen.resultCode);
          }
          onSuccess(results as BasePaymentResponse, provider);
        }
      }
    },
    [onSuccess, onError, on3DSRequired, onRedirect, actions]
  );

  const handlePaypalResponse = useCallback(
    async (results: unknown, provider: ProviderEnum) => {
      if (isPaypalSuccessResponse(results, provider)) {
        onSuccess(results as BasePaymentResponse, provider);
      }
      if (isPaypalFailedResponse(results, provider)) {
        actions.stopPaymentProcess();
        onError(results?.appChargeErrorType, provider);
      }
    },
    [onSuccess, onError, actions]
  );

  const handlePaymentResults = useCallback(
    async (results: unknown, selectedPaymentMethod: PaymentMethodEnum, paymentKeys: PaymentKeys): Promise<unknown> => {
      logInfo(InfoLogs.checkoutPaymentProcessed, {
        selectedPaymentMethod,
        results,
      });
      const provider = getProviderFromPaymentMethod(selectedPaymentMethod, paymentMethods);
      switch (provider) {
        case ProviderEnum.PAYPAL:
          await handlePaypalResponse(results, provider);
          break;
        case ProviderEnum.STRIPE:
          await handleStripeResponse(results, provider, selectedPaymentMethod, paymentKeys.stripe);
          break;

        case ProviderEnum.NUVEI:
          handleNuveiResponse(results, provider);
          break;

        case ProviderEnum.BRAINTREE:
          handleBraintreeResponse(results, provider);
          break;

        case ProviderEnum.ADYEN:
          handleAdyenResponse(results, provider, selectedPaymentMethod);
          break;

        default:
          break;
      }

      return results;
    },
    [
      paymentMethods,
      handleStripeResponse,
      handleNuveiResponse,
      handleBraintreeResponse,
      handleAdyenResponse,
      handlePaypalResponse,
    ]
  );

  return {
    handlePaymentResults,
  };
};
